import { Spinner } from "@/components/ui/Spinner";
import { Surface } from "@/components/ui/Surface";
import { Text } from "@/components/ui/Typography";
import { useModalLayer } from "@/contexts/ModalContext";
import { DURATION } from "@/lib/motion";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useId, type ReactNode } from "react";
import { OVERLAY_BACKDROP, OVERLAY_CARD, overlayCardMotion } from "./overlay-styles";

const MotionSurface = motion.create(Surface);

/** Props of `LoadingOverlay`; `title` is the announced label, `children` extra content under it. */
export type LoadingOverlayProps = {
  open?: boolean;
  title?: ReactNode;
  children?: ReactNode;
};

/** Blocking full-screen loading card (same look as StatusOverlay's loading state). */
export function LoadingOverlay({ open = true, title, children }: LoadingOverlayProps) {
  const reduceMotion = useReducedMotion();
  const { zIndex } = useModalLayer(open);
  const titleId = useId();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="alertdialog"
          aria-modal="true"
          aria-busy="true"
          aria-labelledby={title ? titleId : undefined}
          aria-live="assertive"
          style={{ zIndex }}
          className={OVERLAY_BACKDROP}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DURATION.moderate }}
        >
          <MotionSurface surface="card" className={OVERLAY_CARD} {...overlayCardMotion(reduceMotion)}>
            <div className="flex w-full flex-col items-center gap-4">
              <Spinner size="lg" tone="primary" />
              {title && (
                <Text id={titleId} size="lg" weight="semibold" tone="default">
                  {title}
                </Text>
              )}
              {children}
            </div>
          </MotionSurface>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
